/**
 * DesignPicker — Design selector for funnel steps.
 *
 * Selected: preview + name/version + hover actions.
 * Empty: friendly action cards matching the section style.
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  ImageIcon,
  Pencil,
  Plus,
  ShoppingBag,
  Sparkles,
  Trash2,
  Monitor,
  Smartphone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  buildDesignDisambiguators,
  formatDesignDisambiguator,
  type DesignDisambiguator,
} from '../utils/designDisambiguation';
import { DesignSwitchConfirmDialog, type PendingDesignSwitch } from './DesignSwitchConfirmDialog';

export interface DesignOption {
  key: string;
  instanceId: string;
  pageId?: string;
  name: string;
  configName?: string;
  pluginName?: string;
  screenshotUrl?: string;
  /** Mobile-viewport variant of screenshotUrl, when a capture produced one. */
  screenshotMobileUrl?: string;
  pluginId?: string;
  deploymentId?: string;
  pagePath?: string;
  version?: string;
  otherVersions?: string[];
  updatedAt?: string;
  /** When the design itself was created: what tells same-name designs apart. */
  createdAt?: string;
  editorType?: 'iframe' | 'internal' | 'external' | 'none' | null;
  editorUrl?: string;
}

interface DesignPickerProps {
  designs: DesignOption[];
  selectedKey?: string;
  selectedVersion?: string;
  onSelect: (design: DesignOption) => void;
  onSelectVersion?: (design: DesignOption, version: string) => void;
  onCreateNew?: () => void;
  onBrowseMarketplace?: () => void;
  onEdit?: (designKey: string) => void;
  onDuplicate?: (designKey: string) => void;
  onDelete?: (designKey: string) => void;
  isLoading?: boolean;
  /** A preview screenshot capture is in flight for the active design. */
  isCapturing?: boolean;
  stepType?: string;
  /** Ask before replacing the design a live checkout step already has. */
  confirmSwitch?: boolean;
}

/** Studio-built designs open in the in-app editor; everything else is a legacy plugin. */
const isStudioDesign = (d: DesignOption) => d.editorType === 'iframe' || d.editorType === 'internal';

function DesignThumbnail({
  url,
  alt,
  className,
  isCapturing,
  capturingLabel,
  capturingHint,
}: {
  url?: string;
  alt: string;
  className?: string;
  isCapturing?: boolean;
  capturingLabel?: string;
  capturingHint?: string;
}) {
  if (url) {
    // With a capture in flight, keep showing the previous real image but
    // overlay an "updating" indicator — the old behavior silently showed the
    // stale shot with no hint that a refresh was coming.
    if (isCapturing) {
      return (
        <div className="relative h-full w-full">
          <img src={url} alt={alt} className={cn('h-full w-full object-cover object-top opacity-50', className)} />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-slate-100/70 px-3 text-center dark:bg-slate-900/70">
            <div className="relative h-6 w-6">
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-slate-300 border-t-primary dark:border-slate-600 dark:border-t-primary" />
            </div>
            {capturingLabel && (
              <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">{capturingLabel}</span>
            )}
            {capturingHint && (
              <span className="max-w-[200px] text-[9px] leading-tight text-slate-500 dark:text-slate-400">{capturingHint}</span>
            )}
          </div>
        </div>
      );
    }
    return <img src={url} alt={alt} className={cn('h-full w-full object-cover object-top', className)} />;
  }
  // No thumbnail yet. If a capture is in flight, show a "Generating preview…"
  // spinner so the panel reads as "working" — and make clear it's optional,
  // not something the user has to wait on before saving.
  if (isCapturing) {
    return (
      <div className={cn('flex h-full w-full flex-col items-center justify-center gap-1.5 px-3 text-center bg-slate-100 dark:bg-slate-800', className)}>
        <div className="relative h-7 w-7">
          <div className="absolute inset-0 animate-spin rounded-full border-2 border-slate-300 border-t-primary dark:border-slate-600 dark:border-t-primary" />
        </div>
        {capturingLabel && (
          <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{capturingLabel}</span>
        )}
        {capturingHint && (
          <span className="max-w-[200px] text-[9px] leading-tight text-slate-400 dark:text-slate-500">{capturingHint}</span>
        )}
      </div>
    );
  }
  return (
    <div className={cn('flex h-full w-full items-center justify-center bg-slate-100 dark:bg-slate-800', className)}>
      <ImageIcon className="h-5 w-5 text-slate-300 dark:text-slate-600" />
    </div>
  );
}

export function DesignPicker({
  designs,
  selectedKey,
  selectedVersion,
  onSelect,
  onSelectVersion,
  onCreateNew,
  onBrowseMarketplace,
  onEdit,
  onDuplicate,
  onDelete,
  isLoading,
  isCapturing,
  confirmSwitch,
}: DesignPickerProps) {
  const { t, i18n } = useTranslation('storefront');
  const [pendingSwitch, setPendingSwitch] = useState<PendingDesignSwitch<DesignOption> | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [versionMenuFor, setVersionMenuFor] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Hero-card device toggle (desktop default). Only shown when the previewed
  // design has a mobile screenshot to switch to.
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const hoverLocked = useRef(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const selectedDesign = React.useMemo(
    () => designs.find((d) => d.key === selectedKey),
    [designs, selectedKey],
  );

  const disambiguators = React.useMemo(
    () => buildDesignDisambiguators(designs, i18n.language),
    [designs, i18n.language],
  );

  const otherDesigns = React.useMemo(
    () => designs.filter((d) => d.key !== selectedKey),
    [designs, selectedKey],
  );

  const hoveredDesign = hoveredKey ? designs.find((d) => d.key === hoveredKey) : null;
  const previewDesign = hoveredDesign || selectedDesign;
  const isPreviewing = !!hoveredDesign && hoveredDesign.key !== selectedKey;

  // Studio designs (iframe/internal editor) are the everyday choice. Older
  // non-Studio plugins (Tagada Native, external editors) only clutter the
  // strip, so they sit under a collapsed "Advanced" section. The active
  // design always stays in the main strip, legacy or not, so the "Active"
  // card never disappears into the fold.
  const { mainDesigns, legacyDesigns } = React.useMemo(() => {
    const main: DesignOption[] = [];
    const legacy: DesignOption[] = [];
    designs.forEach((d) => {
      (isStudioDesign(d) || d.key === selectedKey ? main : legacy).push(d);
    });
    return { mainDesigns: main, legacyDesigns: legacy };
  }, [designs, selectedKey]);

  const applyPick = useCallback((design: DesignOption) => {
    // Lock hover-preview briefly so the strip does not flash the old design
    // while the selection settles.
    hoverLocked.current = true;
    setHoveredKey(null);
    onSelect(design);
    setTimeout(() => { hoverLocked.current = false; }, 400);
  }, [onSelect]);

  const handlePick = useCallback((design: DesignOption) => {
    if (design.key === selectedKey) return;
    if (confirmSwitch) {
      setPendingSwitch({ current: selectedDesign, next: design });
      setIsConfirmOpen(true);
      return;
    }
    applyPick(design);
  }, [selectedKey, confirmSwitch, selectedDesign, applyPick]);

  const handleHoverStart = useCallback((key: string) => {
    if (selectedDesign && !hoverLocked.current) setHoveredKey(key);
  }, [selectedDesign]);

  const handleHoverEnd = useCallback(() => {
    if (!hoverLocked.current) setHoveredKey(null);
  }, []);

  const activeVersion = selectedDesign
    ? (selectedVersion || selectedDesign.version)
    : undefined;
  const allVersions = selectedDesign
    ? [selectedDesign.version, ...(selectedDesign.otherVersions || [])].filter(Boolean) as string[]
    : [];
  const switchableVersions = allVersions.filter((v) => v !== activeVersion);

  if (isLoading) {
    return (
      <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
        <Skeleton className="aspect-[16/9] w-full rounded-lg" />
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-20 flex-shrink-0 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  // ─── Empty state ──────────────────────────────────────────────────
  if (designs.length === 0 && (onCreateNew || onBrowseMarketplace)) {
    return (
      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
        {onCreateNew && (
          <button
            type="button"
            onClick={onCreateNew}
            className="flex w-full items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-3 text-left transition-all hover:border-primary/40 hover:bg-primary/10 dark:border-primary/20 dark:bg-primary/10 dark:hover:border-primary/40"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-white">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{t('designPicker.chooseDesign')}</p>
              <p className="text-xs text-primary/70 dark:text-primary/60">
                {t('designPicker.pickFromTemplates')}
              </p>
            </div>
          </button>
        )}
        {onBrowseMarketplace && (
          <button
            type="button"
            onClick={onBrowseMarketplace}
            className="flex w-full items-center gap-3 rounded-lg border border-amber-200/60 bg-amber-50/40 px-3.5 py-3 text-left transition-all hover:border-amber-300 hover:bg-amber-50 dark:border-amber-700/30 dark:bg-amber-950/20 dark:hover:border-amber-600/50"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
              <ShoppingBag className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{t('designPicker.importTemplate')}</p>
              <p className="text-xs text-amber-600/70 dark:text-amber-400/60">
                {t('designPicker.browseAndInstall')}
              </p>
            </div>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>

      {/* ─── Selected design preview ──────────────────────────── */}
      {selectedDesign ? (
        <div className={cn(
          'group/hero rounded-lg border bg-white transition-colors dark:bg-slate-900',
          isPreviewing
            ? 'border-primary/40 ring-1 ring-primary/20'
            : 'border-slate-200 dark:border-slate-700',
        )}>
          <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-lg bg-slate-50 dark:bg-slate-800">
            <DesignThumbnail
              url={
                previewDevice === 'mobile' && previewDesign?.screenshotMobileUrl
                  ? previewDesign.screenshotMobileUrl
                  : previewDesign?.screenshotUrl
              }
              alt={previewDesign?.name || ''}
              className="transition-opacity duration-200"
              // Only the active design's capture corresponds to this step;
              // suppress the spinner while hovering/previewing another design.
              isCapturing={!isPreviewing && isCapturing}
              capturingLabel={t('designPicker.capturing')}
              capturingHint={t('designPicker.capturingHint')}
            />

            {/* Device toggle — mirrors the flow-canvas node switcher */}
            {previewDesign?.screenshotMobileUrl && (
              <div
                className="absolute bottom-2 right-2 z-10 flex overflow-hidden rounded border border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900/95"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  title={t('flowStep.previewDesktop')}
                  onClick={() => setPreviewDevice('desktop')}
                  className={cn(
                    'flex h-5 w-6 items-center justify-center transition-colors',
                    previewDevice === 'desktop'
                      ? 'bg-primary text-white'
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
                  )}
                >
                  <Monitor className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  title={t('flowStep.previewMobile')}
                  onClick={() => setPreviewDevice('mobile')}
                  className={cn(
                    'flex h-5 w-6 items-center justify-center transition-colors',
                    previewDevice === 'mobile'
                      ? 'bg-primary text-white'
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
                  )}
                >
                  <Smartphone className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Badge: "Active" or "Preview" */}
            <div className={cn(
              'absolute left-2 top-2 flex items-center gap-1.5 rounded-full px-2 py-0.5 shadow-sm transition-colors',
              isPreviewing ? 'bg-amber-500' : 'bg-primary',
            )}>
              {isPreviewing ? (
                <span className="text-[10px] font-medium text-white">{t('designPicker.preview')}</span>
              ) : (
                <>
                  <Check className="h-3 w-3 text-white" />
                  <span className="text-[10px] font-medium text-white">{t('designPicker.active')}</span>
                </>
              )}
            </div>

            {/* Hover actions — only when not previewing another design */}
            {!isPreviewing && (
              <div className="absolute inset-0 flex flex-wrap content-center items-center justify-center gap-1.5 px-2 bg-black/0 opacity-0 transition-all group-hover/hero:bg-black/30 group-hover/hero:opacity-100">
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(selectedDesign.key)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-lg backdrop-blur-sm transition-transform hover:scale-105 hover:bg-white"
                  >
                    <Pencil className="h-3 w-3" />
                    {t('designPicker.edit')}
                  </button>
                )}
                {onDuplicate && selectedDesign.editorType === 'iframe' && (
                  <button
                    type="button"
                    onClick={() => onDuplicate(selectedDesign.key)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-lg backdrop-blur-sm transition-transform hover:scale-105 hover:bg-white"
                  >
                    <Copy className="h-3 w-3" />
                    {t('designPicker.duplicate')}
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(selectedDesign.key)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-medium text-red-600 shadow-lg backdrop-blur-sm transition-transform hover:scale-105 hover:bg-white"
                  >
                    <Trash2 className="h-3 w-3" />
                    {t('designPicker.remove')}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className={cn(
                'truncate text-sm font-medium transition-colors',
                isPreviewing ? 'text-primary' : 'text-slate-800 dark:text-slate-200',
              )}>
                {previewDesign?.name}
              </p>
              {isPreviewing ? (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  {t('designPicker.clickToApply')}
                </p>
              ) : disambiguators.has(selectedDesign.key) ? (
                <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">
                  {formatDesignDisambiguator(disambiguators.get(selectedDesign.key)!)}
                </p>
              ) : (
                // The plugin's name, as a subtitle, disambiguates a
                // MARKETPLACE design: "Checkout" installed from "Acme
                // Checkout" is worth saying twice.
                //
                // For a design built in Studio it is not a name at all — the
                // deploy derives the plugin id from it, so it carries the
                // random token the CRM stapled on at creation, and the card
                // read "ONYX & OAK Checkout / Checkout p8x2". One title now,
                // and the id stays where ids belong (#TAG-2381).
                selectedDesign.editorType !== 'iframe' &&
                selectedDesign.pluginName && selectedDesign.pluginName !== selectedDesign.name && (
                  <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">
                    {selectedDesign.pluginName}
                  </p>
                )
              )}
            </div>
            {!isPreviewing && activeVersion && (
              <div className="relative flex shrink-0 items-center">
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  v{activeVersion}
                </span>
                {switchableVersions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setVersionMenuFor(versionMenuFor === selectedDesign.key ? null : selectedDesign.key)}
                    className="ml-0.5 inline-flex items-center rounded px-0.5 text-slate-400 hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800"
                    title={`${switchableVersions.length} other version${switchableVersions.length > 1 ? 's' : ''}`}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                )}
                {versionMenuFor === selectedDesign.key && switchableVersions.length > 0 && (
                  <div className="absolute right-0 top-full z-50 mt-1 min-w-[120px] rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {t('designPicker.switchVersion')}
                    </p>
                    {switchableVersions.map((v) => (
                      <button
                        key={v}
                        type="button"
                        className="flex w-full items-center rounded-md px-2 py-1.5 text-xs text-slate-600 hover:bg-primary/10 hover:text-primary dark:text-slate-400 dark:hover:bg-primary/10 dark:hover:text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setVersionMenuFor(null);
                          if (onSelectVersion) onSelectVersion(selectedDesign, v);
                        }}
                      >
                        v{v}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : designs.length > 0 ? (
        <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-4 text-center dark:border-amber-600/40 dark:bg-amber-950/20">
          <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
            {t('designPicker.selectDesignBelow')}
          </p>
          <p className="mt-0.5 text-xs text-amber-600/70 dark:text-amber-500/70">
            {t('designPicker.choosePageLook')}
          </p>
          {onCreateNew && (
            <button
              type="button"
              onClick={onCreateNew}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20 dark:bg-primary/20 dark:hover:bg-primary/30"
            >
              <Plus className="h-3 w-3" />
              {t('designPicker.orStartFromScratch')}
            </button>
          )}
        </div>
      ) : null}

      {/* ─── Filmstrip ────────────────────────────────────────── */}
      {(otherDesigns.length > 0 || onCreateNew || onBrowseMarketplace) && (
        <div className="space-y-2">
          {/* Header: label + the create/import actions. They used to be the
              last cards of the strip, so past ~3 designs they scrolled out of
              view and nobody saw a new template could be started from here. */}
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              {selectedDesign && otherDesigns.length > 0 ? t('designPicker.switchDesign') : ''}
            </p>
            {(onCreateNew || onBrowseMarketplace) && (
              <div className="flex shrink-0 items-center gap-1">
                {onCreateNew && (
                  <button
                    type="button"
                    onClick={onCreateNew}
                    className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-600 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-slate-600 dark:text-slate-300 dark:hover:border-primary"
                  >
                    <Plus className="h-3 w-3" />
                    {t('designPicker.new')}
                  </button>
                )}
                {onBrowseMarketplace && (
                  <button
                    type="button"
                    onClick={onBrowseMarketplace}
                    className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-600 transition-colors hover:border-amber-500 hover:bg-amber-50 hover:text-amber-700 dark:border-slate-600 dark:text-slate-300 dark:hover:border-amber-500 dark:hover:bg-amber-950/30 dark:hover:text-amber-400"
                  >
                    <ShoppingBag className="h-3 w-3" />
                    {t('designPicker.import')}
                  </button>
                )}
              </div>
            )}
          </div>

          {mainDesigns.length > 0 && (
            <DesignStrip
              designs={mainDesigns}
              selectedKey={selectedKey}
              hoveredKey={hoveredKey}
              compact={!!selectedDesign}
              onPick={handlePick}
              onHoverStart={handleHoverStart}
              onHoverEnd={handleHoverEnd}
              disambiguators={disambiguators}
            />
          )}

          {/* Legacy (non-Studio) designs stay reachable, but out of the way */}
          {legacyDesigns.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowLegacy((v) => !v)}
                aria-expanded={showLegacy}
                className="flex w-full items-center gap-1.5 rounded-md px-1 py-1 text-[10px] font-medium text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
              >
                <ChevronRight className={cn('h-3 w-3 transition-transform', showLegacy && 'rotate-90')} />
                <Archive className="h-3 w-3" />
                <span className="truncate">
                  {t('designPicker.advanced')} · {t('designPicker.legacyDesigns', { count: legacyDesigns.length })}
                </span>
              </button>
              {showLegacy && (
                <div className="mt-1 space-y-1.5">
                  <p className="px-1 text-[10px] text-slate-400 dark:text-slate-500">
                    {t('designPicker.legacyDesignsHint')}
                  </p>
                  <DesignStrip
                    designs={legacyDesigns}
                    selectedKey={selectedKey}
                    hoveredKey={hoveredKey}
                    compact={!!selectedDesign}
                    onPick={handlePick}
                    onHoverStart={handleHoverStart}
                    onHoverEnd={handleHoverEnd}
                    disambiguators={disambiguators}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <DesignSwitchConfirmDialog
        open={isConfirmOpen}
        pending={pendingSwitch}
        onConfirm={(design) => {
          setIsConfirmOpen(false);
          applyPick(design);
        }}
        onClose={() => setIsConfirmOpen(false)}
      />
    </div>
  );
}

interface DesignStripProps {
  designs: DesignOption[];
  selectedKey?: string;
  hoveredKey: string | null;
  /** Smaller thumbs when a hero card is shown above the strip. */
  compact: boolean;
  onPick: (design: DesignOption) => void;
  onHoverStart: (key: string) => void;
  onHoverEnd: () => void;
  /** Short id and date per design key, for designs whose name another design shares. */
  disambiguators: Map<string, DesignDisambiguator>;
}

/** Horizontal, scrollable row of design thumbnails with hover arrows past 3 items. */
function DesignStrip({
  designs,
  selectedKey,
  hoveredKey,
  compact,
  onPick,
  onHoverStart,
  onHoverEnd,
  disambiguators,
}: DesignStripProps) {
  const { t } = useTranslation('storefront');
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.6;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  }, []);

  const arrowClass = 'absolute top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-md ring-1 ring-slate-200 opacity-0 transition-opacity group-hover/strip:opacity-100 hover:bg-white dark:bg-slate-800/90 dark:ring-slate-700';

  return (
    <div className="relative group/strip">
      {designs.length > 3 && (
        <>
          <button type="button" onClick={() => scroll('left')} className={cn(arrowClass, '-left-1')}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => scroll('right')} className={cn(arrowClass, '-right-1')}>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </>
      )}

      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-none pb-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {designs.map((design) => {
          const isSelected = design.key === selectedKey;
          const description = disambiguators.get(design.key);
          return (
            <button
              key={design.key}
              type="button"
              onClick={() => onPick(design)}
              onMouseEnter={() => onHoverStart(design.key)}
              onMouseLeave={onHoverEnd}
              className={cn(
                'group/thumb relative flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all',
                isSelected
                  ? 'border-primary shadow-sm'
                  : hoveredKey === design.key
                    ? 'border-primary/40 shadow-sm'
                    : 'border-slate-200 hover:border-primary/40 hover:shadow-sm dark:border-slate-700',
                compact ? 'w-[80px]' : 'w-[100px]',
              )}
            >
              <div className={cn(
                'overflow-hidden bg-slate-50 dark:bg-slate-800',
                compact ? 'aspect-[4/3]' : 'aspect-[16/10]',
              )}>
                <DesignThumbnail url={design.screenshotUrl} alt={design.name} />
              </div>
              <div className="px-1.5 py-1">
                <p className="truncate text-[10px] font-medium text-slate-600 dark:text-slate-300">
                  {design.name}
                </p>
                {description && (
                  <div className="-mx-1 text-[10px] leading-tight text-slate-400 dark:text-slate-500" title={formatDesignDisambiguator(description)}>
                    {[description.shortId, description.configName, description.date].filter(Boolean).map((line) => (
                      <p key={line} className="break-all">{line}</p>
                    ))}
                  </div>
                )}
                {isSelected && (
                  <p className="-mx-1 text-[10px] font-medium leading-tight text-primary">
                    {t('designPicker.usedByThisStep')}
                  </p>
                )}
              </div>
              <div className={cn(
                'absolute inset-0 flex items-center justify-center transition-all',
                isSelected
                  ? 'bg-primary/10 opacity-100'
                  : 'bg-primary/0 opacity-0 group-hover/thumb:bg-primary/10 group-hover/thumb:opacity-100',
              )}>
                <div className="rounded-full bg-primary p-1 shadow-sm">
                  <Check className="h-3 w-3 text-white" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
